import _ from 'lodash';
import { format } from 'date-fns';
import {
  SIGNATURE,
  DEFAULT_MAX_LINES,
  fieldFontPt,
  formFontPt,
  isPageEntry,
  pageKeysOf,
} from './PreviewTool/thresholdConstants';
import imageBase64 from './SignatureImage';
import { buildFieldBoxes, computeFitWidths } from './fillLayout';
import { createMatrixItems, rebuildMatrix } from './matrixItems';
import { columnWidthsOf } from './matrixGrid';
// Dependency-free module, so this cannot reintroduce the import cycle noted
// below.
import { resolveStaticValue } from './PreviewTool/builder/staticFieldOptions';

// Re-exported so existing importers keep working; the implementation moved to
// its own module to break the docOvelayTool <-> builder/elements import cycle.
export { createMatrixItems };

export const THRESHOLD_FONTSIZE = 12;
// Keeps a table cell's text clear of the printed rule on either side. Mirrored
// by CELL_GUTTER_MM in FormFillCanvas so screen and PDF bound cells alike.
export const MATRIX_CELL_GUTTER_MM = 1;
// Floor for shrink-to-fit; below this the text stops being legible anyway.
export const MIN_FONT_SIZE = 4;
// Re-exported for existing importers; defined in thresholdConstants so the
// builder can share it without an import cycle.
export { DEFAULT_MAX_LINES };
/**
 * Stamp the filled values onto `doc`.
 *
 * `skipBackground` leaves the page blank instead of drawing the scanned PNG,
 * producing a transparent text-only layer that mergeSourcePdf then composites
 * over the form's REAL PDF pages. That is what makes the finished report vector
 * — sharp at any zoom — rather than a photo of a form with text on top.
 * Everything else is deliberately untouched: the same coordinates, the same
 * shrink-to-fit, the same checkbox strokes, so the two paths cannot diverge.
 */
export const overlayInfo = (doc, base64, data, realSignature, skipBackground = false) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // `base64` (selectedForm.fileBase64) can be null when the backend has no
  // page images for the form (get_file_base64 returns None) — guard so the
  // preview renders blank instead of throwing on `.length`/indexing.
  const pages = base64 || [];

  // The form's own text size. Every field falls back to it; a field that sets
  // its own still wins.
  const globalFontPt = formFontPt(data);

  // Page entries only. Iterating every root key meant the metadata sitting
  // beside them (`completed`, the form text size) was treated as a page, which
  // would have emitted a stray blank page for each one.
  pageKeysOf(data).forEach((keyPage, pageIndex) => {
    if (pageIndex > 0) {
      doc.addPage();
    }

    // This to prevent a case where we have pages with no data
    if (Object.keys(data[keyPage]).length > 0) {
      // The scanned page image is optional. When the backend can't return it
      // (get_file_base64 -> None because the media file is missing) we still
      // stamp the fields onto a blank page — a report with the values on it is
      // far more useful, and far easier to diagnose, than a blank white PDF.
      const pageImage = pages.length > Number(keyPage) ? pages[keyPage] : null;
      if (pageImage && !skipBackground) {
        doc.addImage(pageImage.base64, 'PNG', 0, 0, pageWidth, pageHeight);
      }

      // Column-clamped widths are only needed by opt-in multiline fields, so
      // this is skipped entirely — no behaviour change, no cost — on every form
      // that does not use the feature.
      const hasMultiline = Object.keys(data[keyPage]).some(
        k => data[keyPage][k] && data[keyPage][k].multiline,
      );
      const fitWidths = hasMultiline ? computeFitWidths(buildFieldBoxes(data, keyPage)) : {};

      Object.keys(data[keyPage]).forEach(key => {
        const item = data[keyPage][key];
        if (key === SIGNATURE) {
          // Signature is resizable: use its stored box, defaulting to 40x10mm.
          const _signatureImage = realSignature || imageBase64;
          const w = Number(item.width) || 40;
          const h = Number(item.height) || 10;
          doc.addImage(_signatureImage, 'PNG', Number(item.left), Number(item.top), w, h);
        } else if (item.top) {
          // Fields carry an optional per-field font size (set by resizing) and an
          // optional width.
          // `${null}` is the string "null", and jsPDF will happily print it —
          // that is how a missing permit number reached an issued report. Every
          // draw below interpolates `text`, so normalise once here rather than
          // trusting each call site. Falsy-but-real values (0) must survive, so
          // this tests for null/undefined specifically.
          const { top, left } = item;
          const text = item.text ?? '';
          const baseSize = fieldFontPt(item, globalFontPt);
          doc.setFontSize(baseSize);
          const marginRight = 3;

          if (item.multiline) {
            // OPT-IN ONLY. Wrapping flows downward, and on a dense form the
            // fields are 5-6mm apart while a 12pt line is ~4.9mm — so a second
            // line lands on the next field's row. Only an admin who can see the
            // blank space below a field may turn this on, which is also why the
            // column-clamped box width below is confined to this branch: every
            // existing field keeps the exact single-line behaviour it had.
            const boxWidth =
              Number(item.width) > 0
                ? Number(item.width)
                : fitWidths[key] || pageWidth - Number(left) - marginRight;
            const maxLines = Math.max(1, Number(item.maxLines) || DEFAULT_MAX_LINES);

            let size = baseSize;
            let lines = doc.splitTextToSize(`${text}`, boxWidth);
            // Wrap first, shrink only as the backstop, so a pasted essay still
            // cannot run down the page over the rest of the form.
            while (lines.length > maxLines && size > MIN_FONT_SIZE) {
              size = Math.max(MIN_FONT_SIZE, size - 0.5);
              doc.setFontSize(size);
              lines = doc.splitTextToSize(`${text}`, boxWidth);
            }
            doc.text(lines.slice(0, maxLines), left, top);
          } else {
            // Unchanged: shrink the font so a long value fits on ONE line within
            // its box — bounded to the page edge by default.
            const boxWidth =
              Number(item.width) > 0 ? Number(item.width) : pageWidth - Number(left) - marginRight;
            const textWidth = doc.getTextWidth(`${text}`);
            if (boxWidth > 0 && textWidth > boxWidth) {
              doc.setFontSize(Math.max(MIN_FONT_SIZE, baseSize * (boxWidth / textWidth)));
            }
            doc.text(`${text}`, left, top);
          }
        }
        if (item.items) {
          // A selected option stamps a bare "x" and nothing else. No square, no
          // circle outline: the scanned form already prints its own controls,
          // so drawing one on top doubled them up.
          //
          // Radio and checkbox both stamp the "x" — by request. A filled dot for
          // single-choice groups was tried and reverted; the distinction only
          // governs how many options can be picked, which is enforced while
          // filling the form, not while stamping it.
          item.items.forEach(i => {
            const checked = `${i.text}`.trim() === 'x';
            if (!checked) return;

            const size = fieldFontPt(i, globalFontPt) * 0.3528; // pt -> mm
            const x0 = Number(i.left);
            const y0 = Number(i.top) - size; // sit the mark above the baseline anchor
            const p = size * 0.22;
            doc.setLineWidth(0.25);
            doc.line(x0 + p, y0 + p, x0 + size - p, y0 + size - p);
            doc.line(x0 + size - p, y0 + p, x0 + p, y0 + size - p);
          });
        }

        if (item.matrix) {
          // Table cells render at the default size (resizing a table changes its
          // grid spacing, not the font), shrunk to fit THEIR OWN column.
          //
          // They used to be stamped with no width bound at all, so a long value
          // printed straight over the neighbouring column's rule — the same
          // overflow the inspector saw on screen. Bounding both from the one set
          // of column widths is what keeps the two in step.
          const widths = columnWidthsOf(item);
          for (let i = 0; i < item.matrix.length; i++) {
            for (let j = 0; j < item.matrix[i].length; j++) {
              const matItem = item.matrix[i][j];
              // Same normalisation as the plain-field path: an empty cell must
              // print nothing, never the string "null".
              const { top, left } = matItem;
              const text = matItem.text ?? '';
              const boxWidth = Math.max((widths[j] ?? 0) - MATRIX_CELL_GUTTER_MM, 0);
              const cellBase = fieldFontPt(item, globalFontPt);
              doc.setFontSize(cellBase);
              const cellWidth = doc.getTextWidth(`${text}`);
              if (boxWidth > 0 && cellWidth > boxWidth) {
                doc.setFontSize(Math.max(MIN_FONT_SIZE, cellBase * (boxWidth / cellWidth)));
              }
              doc.text(`${text}`, left, top);
            }
          }
        }
      });
    }
  });
};

const getNowDate = () => {
  const now = new Date();
  return format(now, 'MM/dd/yyyy');
};
// Copy the answers an inspection already holds onto a FRESH copy of the
// template, so layout comes from the template and values from the inspection.
//
// An inspection stores its own copy of the markup, carrying both the layout and
// the inspector's answers. Returning that copy verbatim — which is what
// clearTemplateTexts used to do whenever one existed — meant a Super Admin's
// edit (moving a checkbox, resizing a field, adding or deleting one) only ever
// reached inspections created AFTER the edit; existing ones were frozen at
// whatever the template looked like the day they were made.
//
// Fields the admin added show up blank; fields the admin deleted disappear
// along with anything typed into them. Checkbox options and matrix cells are
// matched BY INDEX, the same way toggleGroupItem identifies them.
const rebaseAnswers = (template, saved) => {
  const out = {};

  // Root metadata is LAYOUT and comes from the template — the form's text size
  // included, so changing it in the builder reaches inspections that already
  // exist. `completed` is the inspector's own flag and is deliberately left to
  // the carry-over below, which takes it from the saved copy.
  Object.keys(template).forEach(key => {
    if (isPageEntry(template[key]) || key === 'completed') return;
    out[key] = template[key];
  });

  pageKeysOf(template).forEach(tab => {
    const savedPage = saved[tab] || {};
    const page = {};

    Object.keys(template[tab]).forEach(key => {
      const field = template[tab][key];
      const savedField = savedPage[key];

      // `completed` is a page-level flag sitting alongside the fields, not a
      // field of its own — carry the inspection's value across.
      if (key === 'completed' || !field || typeof field !== 'object') {
        page[key] = savedPage[key] !== undefined ? savedPage[key] : field;
        return;
      }

      // A field the admin has just added: no saved answer, leave it blank.
      if (!savedField || typeof savedField !== 'object') {
        page[key] = field;
        return;
      }

      if (Array.isArray(field.items)) {
        const savedItems = Array.isArray(savedField.items) ? savedField.items : [];
        page[key] = {
          ...field,
          items: field.items.map((box, i) =>
            (savedItems[i] ? { ...box, text: savedItems[i].text } : box)),
        };
        return;
      }

      if (Array.isArray(field.matrix)) {
        const savedMatrix = Array.isArray(savedField.matrix) ? savedField.matrix : [];
        page[key] = {
          ...field,
          matrix: field.matrix.map((row, r) =>
            row.map((cell, c) => {
              const savedCell = savedMatrix[r] && savedMatrix[r][c];
              return savedCell ? { ...cell, text: savedCell.text } : cell;
            })),
        };
        return;
      }

      // A STATIC field is auto-filled, so the value just resolved from the
      // inspection wins over whatever happens to be saved.
      //
      // These used to fall through to the line below and keep the saved text,
      // which meant a static field could never populate once an empty value had
      // been stored: adding the mapping after the inspection already existed, or
      // opening the report before an inspector was assigned, left it blank
      // permanently. It also froze the old name onto the report when an
      // inspection was reassigned.
      //
      // 'NOW' is the deliberate exception — it is stamped once, on the day the
      // report was made, and must not be silently re-dated.
      if (field.isStatic && field.fieldName !== 'NOW') {
        page[key] = field;
        return;
      }

      // Plain fields: whatever was typed stays put.
      page[key] = 'text' in savedField ? { ...field, text: savedField.text } : field;
    });

    out[tab] = page;
  });

  // Carry across anything the template does not account for.
  //
  // Rebasing onto the template used to mean "template keys only", which quietly
  // DESTROYED answers: an inspection type can have several forms attached, the
  // editor picks whichever the API listed first, and two near-identical
  // templates differ by a few field keys. Opening an inspection under the other
  // template dropped every answer whose key that template lacked — and autosave
  // then wrote the shortened markup back, so the loss was permanent.
  //
  // A field the admin genuinely deleted keeps its saved value here rather than
  // vanishing. That is the deliberate trade: a stale value an admin can see and
  // clear beats silently deleting an inspector's completed report.
  Object.keys(saved).forEach(tab => {
    const savedPage = saved[tab];
    // Top-level, non-page entries (`completed`) were being dropped too.
    if (!savedPage || typeof savedPage !== 'object' || Array.isArray(savedPage)) {
      if (!(tab in out)) out[tab] = savedPage;
      return;
    }
    if (!(tab in out)) {
      // A whole page the template does not define — keep it verbatim.
      out[tab] = savedPage;
      return;
    }
    Object.keys(savedPage).forEach(key => {
      if (!(key in out[tab])) out[tab][key] = savedPage[key];
    });
  });

  return out;
};

/**
 * Fill in the static fields whose value does not depend on an inspection.
 *
 * The builder's Preview PDF has no inspection to resolve against, so every
 * static field printed its `-test` placeholder — including ones like
 * "Elite Permits Name" that are the same on every report and could perfectly
 * well be shown.
 *
 * CONSTANT mappings only, which is what makes this safe to run on the
 * inspector's preview too: a mapping that reads anything off the inspection
 * resolves to empty against `{}` and is skipped, keeping whatever was already
 * stamped. 'NOW' is deliberately NOT resolved here — a static date is stamped
 * once when the report is made, and re-resolving it would silently re-date an
 * old report in its own preview.
 */
export const resolveConstantStatics = data => {
  if (!data) return data;

  const out = { ...data };
  pageKeysOf(data).forEach(tab => {
    const page = { ...data[tab] };
    let changed = false;

    Object.keys(page).forEach(key => {
      const item = page[key];
      if (!item || !item.isStatic || item.fieldName === 'NOW') return;
      const resolved = resolveStaticValue({}, item.fieldName);
      if (!resolved) return;
      page[key] = { ...item, text: `${resolved}` };
      changed = true;
    });

    if (changed) out[tab] = page;
  });

  return out;
};

/**
 * Tick every checkbox / radio option. TEMPLATE PREVIEW ONLY.
 *
 * The builder's Report Preview is a LAYOUT preview — every text field prints its
 * '-test' placeholder precisely so the admin can see where it lands. Options had
 * no equivalent: a group is stored unticked, an unticked option stamps nothing,
 * so a group the admin had just placed was simply absent from the preview. It
 * read as "the field didn't save", and it was worse after deleting and re-adding
 * a group, because the older groups still carry the 'x' the builder used to seed
 * and so kept showing up.
 *
 * Marking them here rather than seeding 'x' back into the markup keeps the
 * builder canvas honest — a new group still draws as empty controls, not as a
 * pre-answered form — and keeps the inspector's own preview showing only the
 * boxes they actually ticked.
 */
export const markAllOptions = data => {
  if (!data) return data;

  const out = { ...data };
  pageKeysOf(data).forEach(tab => {
    const page = { ...data[tab] };
    let changed = false;

    Object.keys(page).forEach(key => {
      const item = page[key];
      if (!item || !Array.isArray(item.items)) return;
      page[key] = { ...item, items: item.items.map(box => ({ ...box, text: 'x' })) };
      changed = true;
    });

    if (changed) out[tab] = page;
  });

  return out;
};

export const clearTemplateTexts = (data, selectedInspection) => {
  if (!data) {
    return;
  }

  pageKeysOf(data).forEach(tab => {
    Object.keys(data[tab]).forEach((itemKey: any) => {
      if (data[tab][itemKey].isStatic) {
        const text = data[tab][itemKey].fieldName;
        data = {
          ...data,
          [tab]: {
            ...data[tab],
            [itemKey]: {
              ...data[tab][itemKey],
              text:
                text === 'NOW'
                  ? getNowDate()
                  : resolveStaticValue(selectedInspection, text),
              name: itemKey.replace(/ /g, ''),
            },
          },
        };
      } else if (data[tab][itemKey].inputType) {
        // removing all the 'x' from the checkboxes
        data = {
          ...data,
          [tab]: {
            ...data[tab],
            [itemKey]: {
              ...data[tab][itemKey],
              items: data[tab][itemKey].items.map(i => {
                return {
                  ...i,
                  text: '',
                  name: `${new Date().getTime()}_${i.caption.replace(/ /g, '')}`,
                };
              }),
            },
          },
        };
      } else if (data[tab][itemKey].matrix) {
        // Clearing the matrix. Rebuilt FROM THE FIELD so per-column widths
        // survive: passing the gaps alone would re-space the grid evenly and
        // undo the alignment the admin did against the printed table.
        const _matrixItem = data[tab][itemKey];
        data = {
          ...data,
          [tab]: {
            ...data[tab],
            [itemKey]: {
              ...data[tab][itemKey],
              matrix: rebuildMatrix(_matrixItem, ''),
            },
          },
        };
      } else {
        // removing the text values that came from the Admin module
        data = {
          ...data,
          [tab]: {
            ...data[tab],
            [itemKey]: {
              ...data[tab][itemKey],
              text: '',
              name: itemKey.replace(/ /g, ''),
            },
          },
        };
      }
    });
  });

  return selectedInspection.formMarkup ? rebaseAnswers(data, selectedInspection.formMarkup) : data;
};

export const camelCaseToWords = str => {
  const result = str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');

  return result.charAt(0).toUpperCase() + result.slice(1);
};
